import { useMutation } from "urql"

import type {
  LocationCarrierPreferenceItemInput,
  LocationCarrierPreferenceUpsertInput,
  LocationCarrierPreferenceUpsertMutation,
} from "@/graphql/codegen/graphql"
import { LocationCarrierPreferenceUpsertDocument } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface SaveCarrierPreferencesInput {
  shipToLocationId: string
  preferences: LocationCarrierPreferenceItemInput[]
}

interface UseSaveCarrierPreferencesReturn {
  saveCarrierPreferences: (input: SaveCarrierPreferencesInput) => Promise<boolean>
  isSavingCarrierPreferences: boolean
}

export const useSaveCarrierPreferences = (): UseSaveCarrierPreferencesReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)

  const [{ fetching }, executeMutation] = useMutation<
    LocationCarrierPreferenceUpsertMutation,
    { input: LocationCarrierPreferenceUpsertInput }
  >(LocationCarrierPreferenceUpsertDocument)

  const saveCarrierPreferences = async (input: SaveCarrierPreferencesInput): Promise<boolean> => {
    const { data, error } = await executeMutation({ input })
    const output = data?.locationCarrierPreference.upsert
    const hasError = error || output?.__typename === "LocationCarrierPreferenceUpsertFailure"

    if (hasError) {
      notify({
        type: NotificationType.Error,
        silent: false,
        message: error?.message ?? "Failed to save carrier preferences. Please try again.",
      })
      return false
    }

    notify({
      type: NotificationType.Success,
      silent: false,
      content: "Carrier preferences saved successfully.",
    })
    return true
  }

  return {
    saveCarrierPreferences,
    isSavingCarrierPreferences: fetching,
  }
}
